import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Combobox,
  ComboboxInput,
  Dialog,
  DialogPanel,
  DialogBackdrop,
  DialogTitle
} from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useDebouncedCallback } from "use-debounce";
import GetHeroIcon from "../../utils/getHeroIcon";
import useTracking from "../../components/molecules/analytics/hooks/useTracking";
import { useLazyGetSearchSuggestionQuery } from "../../pages/search-page/components/globalSearchSlice";
import { setVariable } from "../../store/slices/searchQuerySlice";
import { queryArgsActions } from "../../store/slices/query-args/queryArgsSlice";
import { RootState } from "../../store/store";
import ResultListItem from "./ResultListItem";

interface Props {
  open: boolean;
  setOpen: (val: boolean) => void;
}

export default function SearchBox({ open, setOpen }: Props) {
  const navigate = useNavigate();
  const [track] = useTracking();
  const dispatch = useDispatch();
  const showSearchResult = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams?.get("searchRequest") ?? "");
  const [searchFlag, setSearchFlag] = useState<boolean>(false);
  const [typing, setTyping] = useState<boolean>(false);
  const user = useSelector((state: RootState) => state.UserInfoSlice);
  const searchQuery = useSelector(
    (state: RootState) => state?.searchQuery.searchQuery
  );
  const searchCatalogArgs = useSelector(
    (state: RootState) => state?.queryArgsSlice?.searchCatalogArgs
  );

  const [
    triggerGetGlobalSearch,
    { data: globalResult, isLoading, isFetching }
  ] = useLazyGetSearchSuggestionQuery();

  useEffect(() => {
    if (typing) {
      triggerGetGlobalSearch(searchCatalogArgs);
    }
  }, [searchCatalogArgs, typing]);

  const debounced = useDebouncedCallback(
    // function
    (value) => {
      dispatch(
        queryArgsActions.searchCatalogArgs({
          ...searchCatalogArgs,
          requestDto: {
            ...searchCatalogArgs?.requestDto,
            searchRequest: value,
            smartSearch: true
          },
          pageNumber: 1
        }) as any
      );
      const newSearchCatalog: any = {
        ...searchCatalogArgs,
        requestDto: {
          ...searchCatalogArgs?.requestDto,
          searchRequest: value
        }
      };

      const newFiltersParam: Partial<any> = {
        searchRequest: value
      };
      Object.keys(newSearchCatalog?.requestDto?.filters).forEach((key: any) => {
        if (newSearchCatalog?.requestDto?.filters[key]) {
          newFiltersParam[key] = newSearchCatalog?.requestDto?.filters[key];
        }
      });
      // console.log("debounce", newFiltersParam);

      setSearchParams(new URLSearchParams(newFiltersParam));
      // will not show search result if user press enter key immediately
      showSearchResult.current && setSearchFlag(true);
      track("onChange", {
        eventName: "onChange",
        object: {
          id: user.profile.id,
          objectType: "CATALOG_SEARCH"
        },
        verb: {
          id: "VIEWED",
          display: {
            "en-US": value
          }
        }
      });
      setTyping(true);
    },
    // delay in ms
    300
  );

  useEffect(() => {
    if (!location?.pathname.includes("/search")) {
      dispatch(setVariable(""));
    }
    if (open == true && searchParams.get("searchRequest") != null) {
      dispatch(setVariable(searchParams.get("searchRequest") ?? ""));
    }
    return () => {
      dispatch(setVariable(""));
    };
  }, [location, open]);

  useEffect(() => {
    showSearchResult.current = false;
  }, [navigate]);

  const handleSearch = () => {
    setTyping(true);
    setOpen(false);
    setSearchFlag(false);
    navigate(
      `/search?searchRequest=${encodeURIComponent(searchQuery)}&smartSearch=true`
    );
    dispatch(
      queryArgsActions.searchCatalogArgs({
        ...searchCatalogArgs,
        requestDto: {
          ...searchCatalogArgs.requestDto,
          filters: {},
          smartSearch: true
        }
      }) as any
    );
  };

  return (
    <Dialog
      className="relative z-[10001]"
      open={open}
      onClose={() => {
        setOpen(false);
        setQuery("");
      }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 backdrop-blur-md bg-secondary/50 dark:bg-secondary-bg/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div
        className="fixed inset-0 z-10 overflow-y-auto p-4 pt-28 sm:p-6 sm:pt-32 md:p-28"
        onClick={() => {
          if (location?.pathname.includes("/search")) {
            handleSearch();
          }
        }}
      >
        <DialogPanel
          transition
          className="mx-auto xl:w-1/2 lg:w-4/6 md:w-3/4 transform  overflow-hidden rounded-xl ring-0 transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <p className="font-display mb-6 px-1 w-full flex flex-wrap justify-center items-center gap-x-2">
            <span className="text-lg sm:text-xl md:text-2xl flex flex-nowrap font-semibold text-white">
              Hey {user?.profile?.name?.firstName || "there"},
            </span>{" "}
            <span className="text-base sm:text-lg md:text-xl flex flex-nowrap font-semibold text-gray-300">
              {searchParams.get("searchRequest")
                ? "continue exploring!"
                : "what do you want to learn today?"}
            </span>
          </p>
          <DialogTitle className="sr-only">Search content</DialogTitle>
          <Combobox>
            <div className="flex bg-bkg rounded-xl shadow-xl  items-center gap-2 px-4">
              <ComboboxInput
                autoFocus
                aria-label="Search content"
                type="search"
                className="h-16 w-full border-0 ring-0 bg-bkg text-secondary placeholder:text-gray-500 focus:ring-0"
                placeholder="Search Content"
                onChange={(event) => {
                  dispatch(setVariable(event.target.value));
                  showSearchResult.current = true;
                  setTyping(false);
                  debounced(event?.target?.value);
                }}
                value={searchQuery}
                onBlur={() => {
                  setQuery("");
                }}
                autoComplete="off"
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" ||
                    (e.key === "Escape" &&
                      location?.pathname.includes("/search"))
                  ) {
                    handleSearch();
                  }
                }}
              />
              <button
                type="button"
                aria-label="Submit search"
                className="flex-1 cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-dark-widget"
                onClick={() => {
                  handleSearch();
                }}
              >
                <MagnifyingGlassIcon
                  className="pointer-events-none size-6 text-green group-hover:text-gray-400"
                  aria-hidden="true"
                />
              </button>
            </div>
            {searchQuery != "" && (
              <button
                type="button"
                className="relative bg-bkg flex w-full items-center p-6 mt-6 rounded-xl cursor-pointer group gap-x-8 text-left hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-dark-widget"
                onClick={() => {
                  handleSearch();
                }}
              >
                <div className="flex items-center justify-center flex-none mt-1 rounded-lg h-11 w-11 bg-secondary/5 group-hover:bg-bkg">
                  <GetHeroIcon
                    name="MagnifyingGlassIcon"
                    className="w-6 h-6"
                    aria-hidden="true"
                    variant="outline"
                  />
                </div>
                {searchQuery && searchQuery?.length > 0 && (
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-50">
                      Search for &nbsp; "{searchQuery}"
                      <span className="absolute inset-0" />
                    </p>
                  </div>
                )}
              </button>
            )}
            {(isLoading || isFetching) && searchQuery !== "" && (
              <div className="animate-pulse">
                {[1, 2, 3].map(() => (
                  <div className="relative bg-gray-200 dark:bg-gray-800 min-h-20 px-4 flex items-center mt-5 rounded-lg group gap-6">
                    <div className="p-2 px-4 rounded-lg h-10 w-10 bg-gray-300 dark:bg-gray-600"></div>
                    <div className="w-full p-2 px-4 rounded-lg h-10 bg-gray-300 dark:bg-gray-600"></div>
                  </div>
                ))}
              </div>
            )}
            {!isLoading &&
              !isFetching &&
              searchQuery != "" &&
              typing &&
              globalResult?.globalSearch?.data?.searchResult?.length !== 0 && (
                <div className=" backdrop-blur-md scroll-py-2 overflow-y-auto text-secondary">
                  {globalResult?.globalSearch?.data?.searchResult
                    ?.slice(0, 3)
                    .map((item: any) => (
                      <ResultListItem setOpen={setOpen} item={item} />
                    ))}
                </div>
              )}

            {!isLoading &&
              !isFetching &&
              searchQuery !== "" &&
              globalResult?.globalSearch?.data?.searchResult?.length === 0 && (
                <p className="p-6 bg-bkg mt-5 rounded-xl text-gray-500">
                  No Search Results Found.
                </p>
              )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
